/**
 * Helper functions to generate human-readable trait effect descriptions
 */

import type { TraitModifier, VeterancyModifier } from '../types/veterancy-cohesion';

type DescribableModifier = Partial<TraitModifier> & Partial<VeterancyModifier>;

/**
 * Get human-readable effect descriptions for a trait modifier
 */
export function getTraitEffectDescriptions(
  traitName: string,
  modifier: DescribableModifier
): string[] {
  const effects: string[] = [];
  
  if (modifier.weaponDamage) {
    const percentValue = (modifier.weaponDamage - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% damage`);
  }

  if (modifier.aimTime) {
    const percentValue = (modifier.aimTime - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% aim time`);
  }

  if (modifier.reloadTime) {
    const percentValue = (modifier.reloadTime - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% reload time`);
  }

  if (modifier.timeBetweenShots) {
    const percentValue = (modifier.timeBetweenShots - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% time between shots`);
  }

  if (modifier.staticAccuracy) {
    const [sign, value] = modifier.staticAccuracy;
    const percent = (value * 100).toFixed(3);
    effects.push(`${sign === '+' ? '+' : '-'}${percent}% static accuracy`);
  }

  if (modifier.motionAccuracy) {
    const [sign, value] = modifier.motionAccuracy;
    const percent = (value * 100).toFixed(3);
    effects.push(`${sign === '+' ? '+' : '-'}${percent}% motion accuracy`);
  }

  if (modifier.suppressionReceived) {
    const percentValue = (modifier.suppressionReceived - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% suppression received`);
  }

  if (modifier.suppressRegen) {
    const perSec = modifier.suppressRegen[1];
    effects.push(`Stress recovery: ${perSec}/s`);
  }

  if (modifier.dodgeBonus) {
    const [sign, value] = modifier.dodgeBonus;
    const percent = (value * 100).toFixed(3);
    effects.push(`${sign === '+' ? '+' : '-'}${percent}% dodge bonus`);
  }

  if (modifier.weaponDispersion) {
    const percentValue = (modifier.weaponDispersion - 1) * 100;
    const percent = percentValue.toFixed(3);
    effects.push(`${percentValue >= 0 ? '+' : ''}${percent}% weapon dispersion`);
  }
  
  if (modifier.hitRollECM !== undefined) {
    const percent = (modifier.hitRollECM * 100).toFixed(3);
    effects.push(`${modifier.hitRollECM >= 0 ? '+' : ''}${percent}% ECM hit roll`);
  }
  
  if (modifier.speed !== undefined) {
    const percent = modifier.speed.toFixed(3);
    effects.push(`${modifier.speed >= 0 ? '+' : ''}${percent}% speed`);
  }
  
  if (modifier.chassisRotationSpeed !== undefined) {
    const percent = modifier.chassisRotationSpeed.toFixed(3);
    effects.push(`${modifier.chassisRotationSpeed >= 0 ? '+' : ''}${percent}% chassis rotation speed`);
  }
  
  if (modifier.turretRotationSpeed !== undefined) {
    const percent = modifier.turretRotationSpeed.toFixed(3);
    effects.push(`${modifier.turretRotationSpeed >= 0 ? '+' : ''}${percent}% turret rotation speed`);
  }
  
  return effects;
}

